import React, { useState, useEffect } from 'react';
import { Card, CardHeader, TextField, Box, CardContent, Divider, Grid, Typography, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditBlogPostPage = () => {
  const { id } = useParams(); // Get the post ID from the URL
  const [editingPostData, setEditingPostData] = useState({ title: '', bu_master: '', description: '', category: '', author: '', image: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false); // State to manage loading for fetch and submit
  const [saving, setSaving] = useState(false); // State to manage loading during save operation
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true); // Start loading when fetching the post
      try {
        const response = await axios.get(`https://3wzg6m6x-5000.asse.devtunnels.ms/api/blog/${id}`);
        console.log('Fetched post data:', response.data); // Log the data to inspect structure
    
        // Map response data to editingPostData fields
        setEditingPostData({
          title: response.data.title || '',
          bu_master: response.data.id_bu_master || '', // Adjust if API uses id_bu_master
          description: response.data.description || '',
          category: response.data.category || '',
          author: response.data.author || '',
          image: response.data.image || ''
        });
    
        setImagePreview(response.data.image);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false); // Stop loading once fetching is complete
      }
    };
    
    const fetchBuMasters = async () => {
      try {
        const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
        setBuMasters(response.data);
      } catch (error) {
        console.error('Error fetching BU Masters:', error);
      }
    };
  
    // Ambil username dari token dan set ke editingPostData.author
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const username = decodedToken.username || 'Unknown';
      setEditingPostData((prevData) => ({
        ...prevData,
        author: username, 
      }));
    }
    
    fetchPost();
    fetchBuMasters();
  }, [id]);
  

  const validateForm = () => {
    const { title, bu_master, description, category } = editingPostData;
    if (!title || !bu_master || !description || !category || !imageFile) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'All fields are required, including an image.',
      });
      return false;
    }
    return true;
  };

  const handleEditPost = async () => {
    if (!validateForm()) return; // Stop if validation fails

    setSaving(true); // Start saving process
    const formData = new FormData();
    formData.append('id', id);
    formData.append('author', editingPostData.author); 
    formData.append('editor', editingPostData.editor);
    formData.append('id_bu_master', editingPostData.bu_master);
    formData.append('description', editingPostData.description);
    formData.append('category', editingPostData.category);
    formData.append('image', imageFile); 
    formData.append('title', editingPostData.title);

    try {
      const token = localStorage.getItem('token');
      await axios.put('https://3wzg6m6x-5000.asse.devtunnels.ms/api/blog/edit', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Blog post updated successfully!',
      });

      navigate('/compro/blog-post'); // Redirect after successful edit
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update blog post!',
      });
    } finally {
      setSaving(false); // Stop saving process
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Edit Blog Post</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={editingPostData.title}
              onChange={(e) => setEditingPostData({ ...editingPostData, title: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              disabled={loading} // Disable input while loading
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} disabled={loading}>
              <InputLabel>BU Master</InputLabel>
              <Select
                value={editingPostData.bu_master}
                onChange={(e) => setEditingPostData({ ...editingPostData, bu_master: e.target.value })}
                label="BU Master"
              >
                {buMasters.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <ReactQuill
              value={editingPostData.description}
              onChange={(value) => setEditingPostData({ ...editingPostData, description: value })}
              modules={{
                toolbar: [
                  [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['bold', 'italic', 'underline'],
                  ['link'],
                  [{ 'align': [] }],
                  ['image'],
                ],
              }}
              style={{ height: '150px', width: '100%' }}
              disabled={loading} // Disable editor while loading
            />
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '56px' }}>
            <TextField
              fullWidth
              label="Category"
              value={editingPostData.category}
              onChange={(e) => setEditingPostData({ ...editingPostData, category: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '40px' }}>
            <TextField
              label="Author"
              variant="outlined"
              fullWidth
              margin="normal"
              value={editingPostData.author}
              InputProps={{
                readOnly: true,
              }}
              sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRadius: 2 }}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} disabled={loading}>
              <InputLabel>Editor Pick</InputLabel>
              <Select
                value={editingPostData.editor}
                onChange={(e) => setEditingPostData({ ...editingPostData, editor: e.target.value })}
                label="Editor Pick"
              >
                <MenuItem value={0}>No</MenuItem>
                <MenuItem value={1}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <input type="file" onChange={handleImageChange} style={{ display: 'none' }} id="image-upload" />
            <label htmlFor="image-upload">
              <Button variant="outlined" fullWidth component="span" sx={{ borderRadius: 2, boxShadow: 2 }} disabled={loading}>
                Upload Image
              </Button>
            </label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={{ marginTop: 16, width: '100%', maxHeight: '200px', objectFit: 'cover' }}
              />
            )}
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 2, fontWeight: 'bold', minWidth: 150 }}
              onClick={handleEditPost}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: 2, minWidth: 150 }}
              onClick={() => navigate('/compro/blog-post')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default EditBlogPostPage;
